import { supabase } from "../lib/supabase.js";
import { compressImage } from "../utils/imageCompressor.js";

/**
 * Uploads a thumbnail to Supabase Storage bucket 'thumbnails'
 * and permanently links it in the public.video_stats database table.
 * Tolerant of parameter order: uploadThumbnail(file, uid) or uploadThumbnail(uid, file).
 */
export async function uploadThumbnail(
  arg1: File | Blob | string,
  arg2: string | File | Blob
): Promise<string> {
  let file: File | Blob;
  let videoUid: string;

  if (typeof arg1 === "string" && (arg2 instanceof File || arg2 instanceof Blob)) {
    videoUid = arg1;
    file = arg2;
  } else if ((arg1 instanceof File || arg1 instanceof Blob) && typeof arg2 === "string") {
    file = arg1;
    videoUid = arg2;
  } else if (typeof arg1 === "string" && typeof arg2 === "string") {
    // Both strings (data URL and UID)
    videoUid = arg2;
    await saveThumbnailToDb(videoUid, arg1);
    return arg1;
  } else {
    throw new Error("Invalid arguments to uploadThumbnail");
  }

  // 1. Compress image file if it's a File
  let uploadFile: File = file as File;
  let localDataUrl = "";

  if (file instanceof File) {
    try {
      const compressed = await compressImage(file);
      uploadFile = compressed.file;
      localDataUrl = compressed.dataUrl;
    } catch {
      uploadFile = file as File;
    }
  }

  const fileExt = (uploadFile.name || "thumb.jpg").split(".").pop() || "jpg";
  const filePath = `${videoUid}-${Date.now()}.${fileExt}`;

  // 2. Try Supabase Storage bucket 'thumbnails'
  try {
    const { error } = await supabase.storage
      .from("thumbnails")
      .upload(filePath, uploadFile, {
        cacheControl: "3600",
        upsert: true,
      });

    if (error) {
      console.warn("[supabase-storage] Upload warning:", error);
      throw error;
    }

    const { data: publicUrlData } = supabase.storage
      .from("thumbnails")
      .getPublicUrl(filePath);

    const publicUrl = publicUrlData.publicUrl;

    // Direct Database Upsert to public.video_stats
    await saveThumbnailToDb(videoUid, publicUrl);

    try {
      localStorage.setItem(`helix_thumb_${videoUid}`, publicUrl);
    } catch {}

    return publicUrl;
  } catch (storageErr) {
    console.warn("[supabase-storage] Storage bucket fallback, using compressed data URL:", storageErr);

    // Fallback: Use compressed Data URL (typically ~60KB)
    let finalDataUrl = localDataUrl;
    if (!finalDataUrl) {
      const reader = new FileReader();
      const promise = new Promise<string>((res) => {
        reader.onloadend = () => res((reader.result as string) || "");
        reader.readAsDataURL(uploadFile);
      });
      finalDataUrl = await promise;
    }

    if (finalDataUrl) {
      await saveThumbnailToDb(videoUid, finalDataUrl);
      try {
        localStorage.setItem(`helix_thumb_${videoUid}`, finalDataUrl);
      } catch {}
      return finalDataUrl;
    }

    throw storageErr;
  }
}

async function saveThumbnailToDb(videoUid: string, thumbUrl: string): Promise<void> {
  try {
    const { error } = await supabase.from("video_stats").upsert(
      {
        video_uid: videoUid,
        video_id: videoUid,
        custom_thumbnail: thumbUrl,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "video_uid" }
    );

    if (error) {
      console.warn("[supabase-db] Upsert warning on video_uid:", error);
      await supabase.from("video_stats").upsert({
        video_id: videoUid,
        custom_thumbnail: thumbUrl,
        updated_at: new Date().toISOString(),
      });
    }
  } catch (err) {
    console.warn("[supabase-db] Database save warning:", err);
  }
}

/**
 * Uploads a custom channel banner to Supabase Storage bucket 'channel-banners'
 * and permanently updates public.creator_profiles database table.
 */
export async function uploadChannelBanner(
  file: File,
  handle: string
): Promise<string> {
  const cleanHandle = handle.replace(/^@/, "").toLowerCase();

  // 1. Compress image to max 1920x1080 WebP (~150KB)
  let uploadFile: File = file;
  let localDataUrl = "";

  try {
    const compressed = await compressImage(file, 1920, 1080, 0.85, "image/webp");
    uploadFile = compressed.file;
    localDataUrl = compressed.dataUrl;
  } catch (err) {
    console.warn("[uploadChannelBanner] Compression fallback:", err);
    uploadFile = file;
  }

  const filePath = `${cleanHandle}/banner_${Date.now()}.webp`;

  // 2. Upload to Supabase Storage bucket 'channel-banners'
  try {
    const { error } = await supabase.storage
      .from("channel-banners")
      .upload(filePath, uploadFile, {
        cacheControl: "3600",
        upsert: true,
        contentType: "image/webp",
      });

    if (error) {
      console.warn("[supabase-storage] Channel banner upload warning:", error);
      throw error;
    }

    const { data: publicUrlData } = supabase.storage
      .from("channel-banners")
      .getPublicUrl(filePath);

    const publicUrl = publicUrlData.publicUrl;

    // 3. Upsert record into public.creator_profiles
    await saveBannerToDb(cleanHandle, publicUrl);

    try {
      localStorage.setItem(`helix_banner_${cleanHandle}`, publicUrl);
    } catch {}

    return publicUrl;
  } catch (storageErr) {
    console.warn("[supabase-storage] Storage bucket error, using local data URL fallback:", storageErr);

    // Fallback: Use compressed data URL
    let finalDataUrl = localDataUrl;
    if (!finalDataUrl) {
      const reader = new FileReader();
      const promise = new Promise<string>((res) => {
        reader.onloadend = () => res((reader.result as string) || "");
        reader.readAsDataURL(uploadFile);
      });
      finalDataUrl = await promise;
    }

    if (finalDataUrl) {
      await saveBannerToDb(cleanHandle, finalDataUrl);
      try {
        localStorage.setItem(`helix_banner_${cleanHandle}`, finalDataUrl);
      } catch {}
      return finalDataUrl;
    }

    throw storageErr;
  }
}

async function saveBannerToDb(cleanHandle: string, bannerUrl: string): Promise<void> {
  try {
    // 1. Update public.creator_profiles
    try {
      await supabase.from("creator_profiles").upsert(
        {
          handle: cleanHandle,
          banner_url: bannerUrl,
          bannerUrl: bannerUrl,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "handle" }
      );
    } catch {}

    // 2. Update public.channels
    try {
      await supabase.from("channels").upsert(
        {
          handle: cleanHandle,
          channel_name: cleanHandle,
          banner_url: bannerUrl,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "handle" }
      );
    } catch {}

    if (typeof localStorage !== "undefined") {
      try {
        localStorage.setItem(`helix_banner_${cleanHandle}`, bannerUrl);
      } catch {}
    }

    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("helix_channel_updated", {
        detail: { handle: cleanHandle, bannerUrl }
      }));
    }
  } catch (err) {
    console.warn("[supabase-db] Banner database save error:", err);
  }
}

/**
 * Uploads a custom channel avatar / logo to Supabase Storage
 * and synchronizes both public.channels and public.creator_profiles.
 */
export async function uploadChannelAvatar(
  file: File,
  handle: string
): Promise<string> {
  const cleanHandle = handle.replace(/^@/, "").toLowerCase();

  let uploadFile: File = file;
  let localDataUrl = "";

  try {
    const compressed = await compressImage(file, 400, 400, 0.85, "image/webp");
    uploadFile = compressed.file;
    localDataUrl = compressed.dataUrl;
  } catch (err) {
    uploadFile = file;
  }

  const filePath = `${cleanHandle}/avatar_${Date.now()}.webp`;

  try {
    const { error } = await supabase.storage
      .from("thumbnails")
      .upload(filePath, uploadFile, {
        cacheControl: "3600",
        upsert: true,
        contentType: "image/webp",
      });

    if (error) {
      console.warn("[supabase-storage] Avatar upload warning:", error);
      throw error;
    }

    const { data: publicUrlData } = supabase.storage
      .from("thumbnails")
      .getPublicUrl(filePath);

    const publicUrl = publicUrlData.publicUrl;

    await saveAvatarToDb(cleanHandle, publicUrl);
    return publicUrl;
  } catch (storageErr) {
    let finalDataUrl = localDataUrl;
    if (!finalDataUrl) {
      const reader = new FileReader();
      const promise = new Promise<string>((res) => {
        reader.onloadend = () => res((reader.result as string) || "");
        reader.readAsDataURL(uploadFile);
      });
      finalDataUrl = await promise;
    }

    if (finalDataUrl) {
      await saveAvatarToDb(cleanHandle, finalDataUrl);
      return finalDataUrl;
    }

    throw storageErr;
  }
}

async function saveAvatarToDb(cleanHandle: string, avatarUrl: string): Promise<void> {
  try {
    try {
      await supabase.from("channels").upsert(
        {
          handle: cleanHandle,
          channel_name: cleanHandle,
          avatar_url: avatarUrl,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "handle" }
      );
    } catch {}

    try {
      await supabase.from("creator_profiles").upsert(
        {
          handle: cleanHandle,
          avatar_url: avatarUrl,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "handle" }
      );
    } catch {}

    if (typeof localStorage !== "undefined") {
      try {
        localStorage.setItem(`helix_avatar_${cleanHandle}`, avatarUrl);
      } catch {}
    }

    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("helix_channel_updated", {
        detail: { handle: cleanHandle, avatarUrl }
      }));
    }
  } catch (err) {
    console.warn("[supabase-db] Avatar database save error:", err);
  }
}


